(ns manage.ad-variant.optimization-ocp.view
  (:require ["@mui/material/Accordion$default" :as Accordion]
            ["@mui/material/AccordionDetails$default" :as AccordionDetails]
            ["@mui/material/AccordionSummary$default" :as AccordionSummary]
            ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/AlertTitle$default" :as AlertTitle]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/LinearProgress$default" :as LinearProgress]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["src/svg/arrows/arrow-trend-up.svg$default" :as ArrowTrendUp]
            ["src/svg/controls/hand-draw.svg$default" :as HandDraw]
            ["src/svg/controls/view-list.svg$default" :as ViewList]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            ["src/svg/inform/lightbulb-coil.svg$default" :as Lightbulb]
            ["src/svg/mathematical/chart-bar-horizontal.svg$default" :as ChartBarHorizontal]
            ["src/svg/objects/eye.svg$default" :as Eye]
            ["src/svg/objects/magic-wand.svg$default" :as MagicWand]
            ["src/svg/person/person-2-outlined.svg$default" :as Person2Outlined]
            ["src/svg/wpp/arrows/trend-ascent.svg$default" :as TrendAscent]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.adset :as adset.q]
            [common.resource.query.decision-tree :as dt.q]
            [common.widgets.card :as card]
            [manage.adset-decision-tree-page.event :as dt-event]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn open-delivery-optimization [rotation-control-enabled?]
  (rf/dispatch [:ocp/open {:id (if rotation-control-enabled?
                                 ::delivery-rotation
                                 ::toggle-optimization)}]))

(def ^:private delivery-rotation-options
  [{:id :aco
    :title "ACO"
    :description "Ad Campaign Optimization uses machine learning to automatically distribute your budget across ad variants based on performance metrics."
    :icon ($ TrendAscent)}
   {:id :even-distribution
    :title "Even Distribution"
    :description "All ad variants will be distributed evenly with equal weight. This mode is ideal for A/B testing where you want each variant to receive the same amount of impressions."
    :icon ($ ViewList)}
   {:id :weighted-distribution
    :title "Weighted Distribution"
    :description "Assign custom weight percentages to each ad variant. This mode is ideal for seasonal splits or contractual delivery obligations."
    :icon ($ ChartBarHorizontal)}])

(def KPIs
  [{:id "ctr"
    :title "CTR"
    :description "Available"
    :icon ($ HandDraw)}
   {:id "vtr"
    :title "VTR"
    :description "Coming soon"
    :disabled true
    :icon ($ Eye)}
   {:id "engagement"
    :title "Engagement"
    :description "Coming soon"
    :disabled true
    :icon ($ Person2Outlined)}])

(defui footer [{:keys [optimization-active?]}]
  ($ Stack
     {:direction "row"
      :justifyContent "flex-end"
      :gap 1}
     ($ Button
        {:variant "text"
         :sx #js {:color "text.primary"}
         :onClick #(rf/dispatch [:ocp/close])}
        "Cancel")
     ($ Button
        {:variant "contained"
         :color (if optimization-active? "error" "primary")
         :type "submit"}
        (str (if optimization-active?
               "Disable" "Enable")
             " optimization"))))

(def ^:private overall-status->title
  {:poor "Low optimization score"
   :moderate "Moderate optimization score"
   :good "Good optimization score"})

(defn- parse-overall-status [status]
  (cond
    (keyword? status) status
    (string? status) (keyword status)
    :else status))

(defn- aco-preflight-query-params [{:keys [configuration]}]
  (when configuration
    (select-keys configuration [:vertical :optimization-target])))

(defui accordion-section-title [{:keys [children]}]
  ($ Typography
     {:sx (fn [^js theme]
            #js {:fontFamily (.. theme -typography -fontFamily)
                 :fontSize "lg"
                 :fontWeight 600})}
     children))

(defui optimization-score-alert [{:keys [overall-score overall-status summary]}]
  (let [status (parse-overall-status overall-status)
        title (get overall-status->title status "Optimization score")]
    ($ Alert
       {:color (if (#{:poor :moderate} status) "warning" "default")
        :icon (if (= status :poor) ($ AlertIcon) ($ Lightbulb))}
       ($ AlertTitle
          (str title
               (when (some? overall-score)
                 (str " (" overall-score "%)"))))
       ($ :span summary))))

(defui recommendations-list [{:keys [recommendations]}]
  ($ Typography
     {:component "ul"
      :variant "body2"
      :sx #js {:pl 2
               :lineHeight "1.75rem"}}
     (for [[idx recommendation] (map-indexed vector recommendations)]
       ($ :li {:key idx} recommendation))))

(defui panel-content []
  (let [url-context (use-subscribe [:url-context])
        {{:keys [configuration]} :data}
        (use-query (adset.q/adset-aco-active-configuration url-context))
        {preflight-score :data}
        (use-query (adset.q/adset-aco-preflight-score
                    (merge url-context (aco-preflight-query-params {:configuration configuration}))))
        {:keys [recommendations overall-status]} preflight-score]
    ($ Stack {:gap 3}
       ($ Typography
          "Optimize creative delivery towards a primary KPI. We will reserve a
         10% portion of delivery as a control. This group delivery will
         have a random distribution.")
       (when preflight-score
         ($ optimization-score-alert preflight-score))
       ($ Stack {:gap 1.5}
          ($ Typography {:sx #js {:fontSize "lg"
                                  :fontWeight 600}}
             "Available KPIs")
          ($ Box
             {:sx #js {:display "grid"
                       :gridTemplateColumns "repeat(3, 1fr)"
                       :gap 1.5}}
             (for [{:keys [id title description icon disabled]} KPIs]
               ($ card/selectable
                  {:key id
                   :id id
                   :value "ctr"
                   :title title
                   :icon icon
                   :disabled disabled
                   :radio? false
                   :sx #js {:p 1
                            ".MuiCardHeader-title" #js {:fontSize "sm"}
                            ".MuiCardHeader-avatar" #js {:mr 1}
                            ".MuiAvatar-root" #js {:width 24
                                                   :height 24}
                            ".MuiCardContent-root" #js {:fontSize "sm"
                                                        :ml 4
                                                        :mt 0}}}
                  description))))

       (when (seq recommendations)
         ($ Accordion
            ($ AccordionSummary
               ($ accordion-section-title "Recommendations")
               (when (= (parse-overall-status overall-status) :poor)
                 ($ Chip
                    {:color "warning"
                     :variant "subtle"
                     :label "Action needed"})))
            ($ AccordionDetails
               ($ recommendations-list {:recommendations recommendations}))))

       ($ Accordion
          ($ AccordionSummary
             ($ accordion-section-title "How it works"))
          ($ AccordionDetails
             ($ Stack {:gap 2}
                ($ Box {:sx #js {:position "relative"}}
                   ($ LinearProgress
                      {:variant "determinate"
                       :value 90
                       :sx #js {:height "40px"
                                :borderRadius 0.5
                                :backgroundColor "grey.70"
                                ".MuiLinearProgress-bar" #js {:backgroundColor "primary.400"}}})
                   ($ Typography
                      {:variant "h3"
                       :color "white"
                       :sx #js {:position "absolute"
                                :top "50%"
                                :left "45%"
                                :transform "translate(-50%, -50%)"}}
                      "90% Optimized")
                   ($ Typography
                      {:variant "h3"
                       :sx #js {:position "absolute"
                                :top "50%"
                                :left "95%"
                                :transform "translate(-50%, -50%)"}}
                      "10%"))
                ($ Box
                   {:sx #js {:display "grid"
                             :gridTemplateColumns "1fr 1fr"
                             :gap 2}}
                   ($ Stack {:gap 1}
                      ($ Stack
                         {:direction "row"
                          :gap 0.5}
                         ($ Typography "Optimized delivery")
                         ($ SvgIcon
                            {:inheritViewBox true}
                            ($ ArrowTrendUp {:width 20 :height 20})))
                      ($ Typography {:variant "caption"}
                         "Algorithm based creative selection optimized for click performance."))
                   ($ Stack {:gap 1}
                      ($ Stack
                         {:direction "row"
                          :alignItems "center"
                          :gap 0.5}
                         ($ Typography "Control group")
                         ($ SvgIcon
                            {:inheritViewBox true}
                            ($ Person2Outlined {:width 20 :height 20})))
                      ($ Typography {:variant "caption"}
                         "Random creative selection to measure optimization effectiveness."))))))
       ($ Paper
          {:component Stack
           :elevation 0
           :gap 0.5
           :sx #js {:p 2
                    :pt 1.5
                    :backgroundColor "primary.100"}}
          ($ Typography {:variant "h3"}
             "Important Notes")
          ($ Typography
             {:component "ul"
              :variant "caption"
              :sx #js {:pl 2
                       :lineHeight "2rem"}}
             ($ :li "Optimization model is updated with performance data on an hourly basis")
             ($ :li "Control group selection is randomized and fixed for the campaign duration")
             ($ :li "Performance comparison will be available in insights after 24-48 hours")
             ($ :li "This setting can be disabled at any time from adset settings"))))))

(defocp delivery-rotation ::delivery-rotation
  {:width "min(720px, 100%)"}
  []
  ($ ocp
     {:title "Ad Variant Rotation"
      :footer ($ Stack
                 {:direction "row"
                  :justifyContent "flex-end"
                  :gap 1}
                 ($ Button
                    {:variant "text"
                     :sx #js {:color "text.primary"}
                     :onClick #(rf/dispatch [:ocp/close])}
                    "Cancel"))}
     ($ Stack {:gap 3}
        ($ Typography {:color "text.secondary"}
           "Configure how ad variants are distributed.")
        (for [{:keys [id title description icon]} delivery-rotation-options]
          ($ card/selectable
             {:key id
              :id id
              :radio? false
              :title (if (= id :aco)
                       ($ Stack {:direction "row"
                                 :gap 1
                                 :alignItems "center"}
                          title
                          ($ Chip {:label "Recommended"
                                   :color "success"
                                   :size "small"
                                   :variant "subtle"}))
                       title)
              :icon icon
              :onChange #(case id
                           :aco (do (rf/dispatch [:ocp/close])
                                    (rf/dispatch [:ocp/open {:id ::toggle-optimization}]))
                           :even-distribution (rf/dispatch [::dt-event/setup-rotation "even"])
                           :weighted-distribution (rf/dispatch [::dt-event/setup-rotation "weighted"])
                           nil)}
             description)))))

(defocp toggle-optimization ::toggle-optimization
  {:width "min(720px, 100%)"}
  []
  (let [url-context (use-subscribe [:url-context])
        {{:keys [test-state id]} :data}
        (use-query (adset.q/adset-aco-active-configuration url-context))
        optimization-active? (= test-state "started")
        {start-aco-mutation :mutate} (use-mutation (dt.q/start-aco-mutation url-context))
        {pause-aco-mutation :mutate} (use-mutation (dt.q/pause-aco-mutation
                                                    (assoc url-context :configuration-id id)))]
    ($ ocp
       {:title (str (if optimization-active?
                      "Disable" "Enable")
                    " creative delivery optimization")
        :footer ($ footer {:optimization-active? optimization-active?})
        :on-submit (fn []
                     (if optimization-active?
                       (rf/dispatch [:modal/open
                                     {:id ::optimization-disable-warning
                                      :props {:on-submit (fn []
                                                           (pause-aco-mutation
                                                            {}
                                                            #js {:onSuccess #(rf/dispatch [:ocp/close])}))}}])
                       (start-aco-mutation {} #js {:onSuccess (fn []
                                                                (rf/dispatch [::dt-event/prepare-aco-rotation])
                                                                (rf/dispatch [:ocp/close]))})))}
       ($ panel-content))))

(defmodal optimization-disable-warning ::optimization-disable-warning
  [{:keys [on-submit]}]
  ($ modal-v2
     {:title "Disable Optimization?"
      :footer ($ :<>
                 ($ Button
                    {:variant "text"
                     :sx #js {:color "text.primary"}
                     :onClick #(rf/dispatch [:modal/close])}
                    "Cancel")
                 ($ Button
                    {:variant "contained"
                     :color "error"
                     :onClick (fn []
                                (rf/dispatch [:modal/close])
                                (on-submit))}
                    "Yes, disable it"))
      :sx #js {:maxWidth 500}}
     ($ :span "Disabling optimization will affect your adset's performance:")
     ($ Stack {:component "ul"
               :sx #js {:pl 2}}
        ($ :li "Creative delivery reverts to standard distribution, typically reducing CTR and engagement.")
        ($ :li "The model stops learning, though historical insights remain available.")
        ($ :li "Restarting requires a 24-48 hour learning period to reach optimal performance."))))

(defmodal optimization-publish-warning ::optimization-publish-warning
  [{:keys [on-submit]}]
  ($ modal-v2
     {:title "Warning"
      :footer ($ :<>
                 ($ Button
                    {:variant "text"
                     :sx #js {:color "text.primary"}
                     :onClick #(rf/dispatch [:modal/close])}
                    "Cancel")
                 ($ Button
                    {:variant "contained"
                     :onClick (fn []
                                (rf/dispatch [:modal/close])
                                (on-submit))}
                    "Publish"))
      :sx #js {:maxWidth 440}}
     ($ :span "Changes to your strategy will reset the algorithm's learning period,
               which may temporarily affect optimization performance.")))

(defui delivery-optimization-box [{:keys [sx]}]
  (let [url-context (use-subscribe [:url-context])
        {{:keys [test-state]} :data} (use-query (adset.q/adset-aco-active-configuration url-context))
        {:keys [adset-id]} url-context
        optimization-active? (= test-state "started")
        alert-dismissed-localstorage-key (str "adset-" adset-id "-aco-dismissed")
        [alert-dismissed set-alert-dismissed] (uix/use-state (js/localStorage.getItem alert-dismissed-localstorage-key))]
    (when (and (not optimization-active?) (not alert-dismissed))
      ($ Alert
         {:icon ($ TrendAscent)
          :color "default"
          :action ($ Stack {:direction "row"}
                     ($ Button
                        {:variant "text"
                         :color "grey"
                         :startIcon ($ MagicWand)
                         :onClick #(rf/dispatch [:ocp/open {:id ::toggle-optimization}])}
                        "Enable")
                     ($ IconButton
                        {:onClick (fn []
                                    (set-alert-dismissed true)
                                    (js/localStorage.setItem alert-dismissed-localstorage-key true))}
                        ($ Icon "close")))
          :sx sx}
         ($ AlertTitle "Creative delivery optimization")
         "Boost performance by enabling optimization"))))
